import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import type { SearchScope } from "common/utils/search";
import { OmniboxSearchResult } from "common-client/generated/graphql";
import {
  searchResultCallbackProps,
  SearchResultProps,
} from "common-client/utils/search";
import { intersectScopes } from "common-client/utils/search";
import { track } from "../../utils/tracking";
import { useRecentSearches } from "./hooks/useRecentSearches";
import {
  INITIAL_SEARCH_QUERY_STATE,
  SearchQueryState,
  useSearchQueryState,
} from "./hooks/useSearchQueryState";
import {
  INITIAL_SEARCH_RESULTS_STATE,
  SearchResultsState,
  useSearchResultsState,
} from "./hooks/useSearchResultsState";

type SearchContextType = {
  accountId?: string;
  enableRecentSearches?: boolean;
  calculateBounds?: boolean;
  scopes: Maybe<Array<SearchScope>>;
  searchQueryState: ReturnType<typeof useSearchQueryState>[0];
  searchQueryStateDispatch: ReturnType<typeof useSearchQueryState>[1];
  searchResultsState: ReturnType<typeof useSearchResultsState>[0];
  searchResultsDispatch: ReturnType<typeof useSearchResultsState>[1];
  getRecentSearches: ReturnType<typeof useRecentSearches>["getRecentSearches"];
  addRecentSearch: ReturnType<typeof useRecentSearches>["addRecentSearch"];
  removeRecentSearch: ReturnType<
    typeof useRecentSearches
  >["removeRecentSearch"];
  handleSearchResultClick: (searchResult: OmniboxSearchResult) => void;
  unwrappedHandleSearchResultClick: (searchResult: SearchResultProps) => void;
  setManualScopes: (scopes: Array<SearchScope> | null) => void;
  showScopeBar: boolean;
  handleSearchClose: () => void;
  resetSearch: () => void;
};

export const DEFAULT_SEARCH_CONTEXT_VALUE: SearchContextType = {
  enableRecentSearches: false,
  calculateBounds: false,
  scopes: null,
  searchQueryState: INITIAL_SEARCH_QUERY_STATE,
  searchQueryStateDispatch: () => {},
  searchResultsState: INITIAL_SEARCH_RESULTS_STATE,
  searchResultsDispatch: () => {},
  getRecentSearches: () => [],
  addRecentSearch: () => {},
  removeRecentSearch: () => {},
  handleSearchResultClick: () => {},
  unwrappedHandleSearchResultClick: () => {},
  setManualScopes: () => {},
  showScopeBar: false,
  handleSearchClose: () => {},
  resetSearch: () => {},
};

export const SearchContext = createContext<SearchContextType>(
  DEFAULT_SEARCH_CONTEXT_VALUE,
);

export const SearchContextProvider = ({
  children,
  handleSearchResultClick,
  handleSearchClose,
  accountId,
  initialQueryState,
  initialResultsState,
  enableRecentSearches = false,
  calculateBounds = false,
  showScopeBar = false,
  scopeBoundary,
}: {
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
  handleSearchClose?: () => void;
  accountId?: string;
  initialQueryState?: SearchQueryState;
  initialResultsState?: SearchResultsState;
  enableRecentSearches?: boolean;
  calculateBounds?: boolean;
  showScopeBar?: boolean;
  scopeBoundary?: Array<SearchScope>;
  children: ReactNode;
}) => {
  const [searchQueryState, searchQueryStateDispatch] =
    useSearchQueryState(initialQueryState);
  const [searchResultsState, searchResultsDispatch] =
    useSearchResultsState(initialResultsState);
  const { getRecentSearches, addRecentSearch, removeRecentSearch } =
    useRecentSearches();

  const [manualScopes, setManualScopes] = useState<Array<SearchScope> | null>(
    null,
  );

  const effectiveScopes = useMemo(
    () => intersectScopes({ scopeBoundary, manualScopes }),
    [scopeBoundary, manualScopes],
  );

  const resetSearch = useCallback(() => {
    searchQueryStateDispatch({ type: "resetSearch" });
    searchResultsDispatch({ type: "resetSearch" });
    setManualScopes(null);
  }, [searchQueryStateDispatch, searchResultsDispatch]);

  const wrappedHandleSearchResultClick = useCallback(
    (searchResult: OmniboxSearchResult) => {
      const isRecentSearch = !searchQueryState.query;
      track("Omnibox Search Result Clicked", {
        isRecentSearch,
        resultType: searchResult.__typename,
        query: searchQueryState.query || undefined,
      });
      if (enableRecentSearches) {
        addRecentSearch(searchQueryState.query ?? "", searchResult);
      }
      handleSearchResultClick(searchResultCallbackProps(searchResult));
      resetSearch();
    },
    [
      handleSearchResultClick,
      resetSearch,
      addRecentSearch,
      enableRecentSearches,
      searchQueryState.query,
    ],
  );

  const wrappedHandleSearchClose = useCallback(() => {
    handleSearchClose?.();
    resetSearch();
  }, [handleSearchClose, resetSearch]);

  return (
    <SearchContext.Provider
      value={{
        accountId,
        enableRecentSearches,
        calculateBounds,
        scopes: effectiveScopes,
        showScopeBar,
        searchQueryState,
        searchQueryStateDispatch,
        searchResultsState,
        searchResultsDispatch,
        getRecentSearches,
        addRecentSearch,
        removeRecentSearch,
        handleSearchResultClick: wrappedHandleSearchResultClick,
        handleSearchClose: wrappedHandleSearchClose,
        unwrappedHandleSearchResultClick: handleSearchResultClick,
        setManualScopes,
        resetSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
